import { TFunction } from 'i18next'
import { z } from 'zod'

import { PropertyManager } from '@/api/property-managers'
import { EmailLink } from '@/components/ui/email-link'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { PhoneLink } from '@/components/ui/phone-link'
import { SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants/ui'
import { formatDateOnly } from '@/utils/date'

import { CountBadgeTooltip } from '../shared/count-badge-tooltip'
import { PersonNameCell } from '../shared/person-name-cell'
import { PropertyManagerSortKey, PropertyManagerSortKeySchema } from './property-managers.sort-keys'

export const PROPERTY_MANAGERS_TABLE_ID = 'property-managers-table'

export const propertyManagersTableFiltersSchema = z.object({
  buildingIds: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  search: z.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: PropertyManagerSortKeySchema.optional(),
  sortOrder: SortOrderSchema.optional(),
  toDate: z.coerce.number().optional(),
})

export type PropertyManagersTableFilters = z.infer<typeof propertyManagersTableFiltersSchema>

export const propertyManagersTableInitialFilters: PropertyManagersTableFilters = {
  buildingIds: undefined,
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  search: '',
  skip: 0,
  sortBy: PropertyManagerSortKey.createdAt,
  sortOrder: 'desc',
  toDate: undefined,
}

export const getPropertyManagersTableColumns = (
  t: TFunction<['organisation']>,
  onOpenDetails: (propertyManagerId: number) => void,
): TableColumn<PropertyManager>[] => [
  {
    id: PropertyManagerSortKey.name,
    render: (propertyManager) => (
      <PersonNameCell subtitle={propertyManager.jobTitle} user={propertyManager.user} />
    ),
    sortKey: PropertyManagerSortKey.name,
    title: t('organisation:propertyManagers.list.columns.name'),
    width: '22rem',
  },
  {
    id: PropertyManagerSortKey.email,
    render: (propertyManager) => (
      <Typography color="black500" size="text-small" weight="medium" breakLines={1}>
        <EmailLink email={propertyManager.user.email} />
      </Typography>
    ),
    sortKey: PropertyManagerSortKey.email,
    title: t('organisation:propertyManagers.list.columns.email'),
    width: '22rem',
  },
  {
    id: PropertyManagerSortKey.phone,
    render: (propertyManager) => (
      <Typography color="black500" size="text-small" weight="medium">
        <PhoneLink phone={propertyManager.phone} />
      </Typography>
    ),
    sortKey: PropertyManagerSortKey.phone,
    title: t('organisation:propertyManagers.list.columns.phone'),
    width: '14rem',
  },
  {
    id: PropertyManagerSortKey.buildings,
    render: ({ buildingIds, id }) =>
      buildingIds.length ? (
        <CountBadgeTooltip
          badgeLabel={t('organisation:propertyManagers.list.buildingsCount', {
            count: buildingIds.length,
          })}
          items={buildingIds.map((building) => building.name)}
          moreLabel={(count) => t('organisation:propertyManagers.list.moreBuildings', { count })}
          onMoreClick={() => onOpenDetails(id)}
        />
      ) : (
        <Typography color="black500" size="text-small" weight="medium">
          {EMPTY_VALUE}
        </Typography>
      ),
    sortKey: PropertyManagerSortKey.buildings,
    title: t('organisation:propertyManagers.list.columns.buildings'),
    width: '13rem',
  },
  {
    id: PropertyManagerSortKey.createdAt,
    render: (propertyManager) => (
      <Typography color="black500" size="text-small" weight="medium">
        {formatDateOnly(new Date(propertyManager.createdAt))}
      </Typography>
    ),
    sortKey: PropertyManagerSortKey.createdAt,
    title: t('organisation:propertyManagers.list.columns.createdAt'),
    width: '11rem',
  },
]
